import { useNavigate } from "react-router"
import { useTranslation } from "react-i18next"
import { AlertCircle } from "lucide-react"
import { Button } from "@f2e/ui/components/base"

import { SUPPORT_EMAIL, getSupportMailto } from "@/lib/support"

export interface ErrorPageProps {
  title?: string
  message?: string | React.ReactNode
  showButton?: boolean
  showSupportLink?: boolean
  redirectTo?: string
  redirectLabel?: string
  redirectReload?: boolean
  topOffset?: string
}

const ErrorPage = ({
  title = "Something went wrong",
  message = "An error occurred. Please try again.",
  showButton = true,
  showSupportLink = true,
  redirectTo = "/",
  redirectLabel = "Go back home",
  redirectReload = true,
  topOffset = "200px",
}: ErrorPageProps) => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const handleButtonClick = () => {
    if (!redirectTo) return

    if (redirectReload) {
      window.location.reload()
    } else {
      navigate(redirectTo)
    }
  }

  return (
    <div
      className="flex items-start justify-center min-h-screen"
      style={{
        paddingTop: topOffset,
      }}
    >
      <div className="flex flex-col items-center gap-4">
        <AlertCircle className="w-12 h-12 text-destructive" />
        <h1 className="text-2xl font-semibold">{title}</h1>
        <div className="p-4 text-center text-lg text-muted-foreground">
          {message}
        </div>
        {showSupportLink && (
          <p className="max-w-md text-center text-sm text-muted-foreground">
            {t("common.support.errorHelp")}{" "}
            <a
              className="font-medium text-primary underline-offset-4 hover:underline"
              href={getSupportMailto(title)}
            >
              {SUPPORT_EMAIL}
            </a>
          </p>
        )}
        {showButton && (
          <Button onClick={handleButtonClick}>{redirectLabel}</Button>
        )}
      </div>
    </div>
  )
}

export default ErrorPage
